import React from 'react';
import { Container as ContainerType } from '../../../types/canvas.types';
import { AddNestedHorizontal } from './AddNestedHorizontal';
import { AddNestedVertical } from './AddNestedVertical';
import { AddNestedBetweenLine } from './AddNestedBetweenLine';
import { useEditor } from '../../../contexts/EditorContext';
import { organizeNestedContainersIntoRows } from '../../../utils/containerHelpers';
import { CollapsibleNestedContainer } from './CollapsibleNestedContainer';

const MAX_NESTING_LEVEL = 2;

type NestedContainerProps = {
    id?: string;
    parentId: string;
    nestedContainers: ContainerType[];
    selectedContainerId: string | null;
    onSelect: (containerId: string) => void;
    onAddNestedHorizontal: (parentId: string) => void;
    onAddNestedVertical: (parentId: string, rowIndex?: number) => void;
    onAddNestedBetween?: (parentId: string, beforeIndex?: number) => void;
    onRemoveComponent?: (componentId: string, containerId: string) => void;
    nestingLevel: number;
    containerConfig?: any;
    onFieldSelect?: (fieldId: string, fieldType: string, containerId: string, parentId?: string) => void;
    selectedFieldId?: string | null;
    onSubmodelSelect?: (submodelId: string, submodelName: string) => void;
    selectedSubmodelId?: string | null;
    onWidgetSelect?: (widgetId: string, widgetType: string, configuration?: any) => void;
    onAddContainer?: (afterContainerId: string) => void;
    onDuplicateContainer?: (containerId: string) => void;
    onDeleteContainer?: (containerId: string) => void;
    onContentUpdate?: (widgetId: string, content: string) => void;
}

export const NestedContainer = ({
    id,
    onAddContainer,
    onDeleteContainer,
    onDuplicateContainer,
    parentId,
    nestedContainers,
    selectedContainerId,
    onSelect,
    onAddNestedHorizontal,
    onAddNestedVertical,
    onAddNestedBetween,
    onRemoveComponent,
    nestingLevel = 1,
    containerConfig,
    onFieldSelect,
    selectedFieldId,
    onSubmodelSelect,
    selectedSubmodelId,
    onWidgetSelect,
    onContentUpdate
}: NestedContainerProps) => {
    const baseId = id || 'nested-container';
    const { setEditorState, editorState } = useEditor();

    // At Level 2, we can add siblings but not children (no Level 3)
    const canAddSiblings = nestingLevel <= MAX_NESTING_LEVEL; 

    const nestedRows = organizeNestedContainersIntoRows(nestedContainers);
    
    const handleAddBetween = (parentId: string, beforeIndex?: number) => {
        console.log(`[handleAddBetween] Called with parentId: ${parentId}, beforeIndex: ${beforeIndex}, nestingLevel: ${nestingLevel}, canAddSiblings: ${canAddSiblings}`);
        if (!canAddSiblings) {
            console.warn(`Cannot add siblings at nesting level ${nestingLevel}`);
            return;
        }
        if (onAddNestedBetween) {
            onAddNestedBetween(parentId, beforeIndex);
        } else {
            onAddNestedHorizontal(parentId);
        }
    };

    const handleAddVertical = (parentId: string, rowIndex: number) => {
        if (!canAddSiblings) {
            console.warn(`Cannot add siblings at nesting level ${nestingLevel}`);
            return;
        }
        console.log(`[handleAddVertical] Adding vertical container at level ${nestingLevel}, parentId: ${parentId}, rowIndex: ${rowIndex}`);
        onAddNestedVertical(parentId, rowIndex);
    };

    const handleRemoveComponent = (componentId: string, containerId: string) => {
        if (onRemoveComponent) {
            onRemoveComponent(componentId, containerId);
        }
    };

    const getSelectionLevel = (containerId: string, selectionPath: string[]): 'primary' | 'secondary' | 'tertiary' | null => {
        const pathIndex = selectionPath.indexOf(containerId);
        if (pathIndex === -1) return null;
        
        const pathLength = selectionPath.length;
        const distanceFromEnd = pathLength - pathIndex - 1;
        
        if (distanceFromEnd === 0) return 'primary';
        if (distanceFromEnd === 1) return 'secondary';
        if (distanceFromEnd === 2) return 'tertiary';
        
        return 'tertiary';
    };

    const isSelected = (containerId: string) => selectedContainerId === containerId;

    const isParentOfSelected = (container: ContainerType): boolean => {
        if (!selectedContainerId || !container.content.nestedContainers) return false;
        
        if (container.content.nestedContainers.some(child => child.id === selectedContainerId)) {
            return true;
        }
        
        return container.content.nestedContainers.some(child => {
            if (child.content.nestedContainers && child.content.nestedContainers.length > 0) {
                return isParentOfSelected(child);
            }
            return false;
        });
    };

    const getAlignmentClass = (alignment: 'left' | 'center' | 'right') => {
        switch (alignment) {
            case 'center': return 'tw-mx-auto';
            case 'right': return 'tw-ml-auto';
            case 'left':
            default: return '';
        }
    };
    
    const getWidthStyle = (container: ContainerType) => {
        const widthStr = container.configuration.width || '100%';
        
        if (widthStr.toString().endsWith('%')) {
            const widthValue = parseInt(widthStr);
            const safeWidth = Math.max(widthValue, 25);
            
            return { 
                width: `${safeWidth}%`, 
                maxWidth: `${safeWidth}%`,
                flex: widthValue < 100 ? `0 0 ${safeWidth}%` : '1 1 100%'
            };
        }
        
        if (widthStr.toString().endsWith('px')) {
            const widthValue = parseInt(widthStr);
            const safeWidth = Math.max(widthValue, 100);
            return { 
                width: `${safeWidth}px`, 
                maxWidth: `${safeWidth}px`,
                flex: `0 0 ${safeWidth}px`
            };
        }
        
        return { 
            width: '100%', 
            maxWidth: '100%',
            flex: '1 1 100%'
        };
    };

    const getContainerHighlightClass = (container: ContainerType) => {
        if (isSelected(container.id)) {
            return 'tw-border-[#1F7AE4] tw-bg-[#F4F5F7] tw-shadow-sm';
        }
        
        const selectionLevel = getSelectionLevel(container.id, editorState.selectionPath);
        
        if (selectionLevel === 'secondary') {
            return 'tw-border-[#90CAF9] tw-bg-[#F8FBFF]';
        } else if (selectionLevel === 'tertiary') {
            return 'tw-border-[#E3F2FD] tw-bg-[#FAFBFF]';
        } else if (isParentOfSelected(container)) {
            return 'tw-border-[#90CAF9] tw-bg-[#F8FBFF]';
        }
        
        return 'tw-border-[#DADBDD] tw-bg-[#F4F5F7]';
    };

    const handleDirectNestedContainerSelect = (containerId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        
        const target = e.target as HTMLElement;
        const isFieldClick = target.closest('[data-field-id]');
        const isSubmodelClick = target.closest('[data-submodel-id]');
        
        if (isFieldClick || isSubmodelClick) {
            return;
        }
        
        (e.currentTarget as HTMLElement).setAttribute('data-container-type', 'nested');
        (e.currentTarget as HTMLElement).setAttribute('data-container-id', containerId);
        
        setEditorState(prev => ({
            ...prev,
            selectedContainerId: containerId
        }));
        
        onSelect(containerId);
    };

    const renderNestedContainer = (nestedContainer: ContainerType) => {
        return (
            <CollapsibleNestedContainer
                nestedContainer={nestedContainer}
                nestingLevel={nestingLevel}
                selectedContainerId={selectedContainerId}
                selectedFieldId={selectedFieldId}
                selectedSubmodelId={selectedSubmodelId}
                getContainerHighlightClass={getContainerHighlightClass}
                getWidthStyle={getWidthStyle}
                getAlignmentClass={getAlignmentClass}
                handleDirectNestedContainerSelect={handleDirectNestedContainerSelect}
                handleRemoveComponent={handleRemoveComponent}
                onFieldSelect={onFieldSelect}
                onSubmodelSelect={onSubmodelSelect}
                onWidgetSelect={onWidgetSelect}
                onContentUpdate={onContentUpdate}
                onAddContainer={onAddContainer}
                onDuplicateContainer={onDuplicateContainer}
                onDeleteContainer={onDeleteContainer}
                onSelect={onSelect}
                onAddNestedHorizontal={onAddNestedHorizontal}
                onAddNestedVertical={onAddNestedVertical}
                onAddNestedBetween={onAddNestedBetween}
                onRemoveComponent={onRemoveComponent}
            />
        );
    };

    return (
    <div id={baseId} data-testid={baseId} className="tw-flex tw-flex-col tw-space-y-2">
        {nestedContainers.length > 0 && nestedRows.map((rowContainers: any, rowIndex: number) => {
            const isFullWidthRow = rowContainers.length === 1 && rowContainers[0].configuration.width === '100%';
            const rowId = `${baseId}-row-${rowIndex}`;

            return (
                <React.Fragment key={`row-${rowIndex}`}>
                    {rowIndex === 0 && canAddSiblings && (
                        <AddNestedBetweenLine
                            id={`${baseId}-add-between-top`}
                            parentId={parentId}
                            beforeIndex={0}
                            onAdd={() => {
                                const firstContainerInRow = rowContainers[0];
                                const beforeIndex = nestedContainers.findIndex(nc => nc.id === firstContainerInRow.id);
                                handleAddBetween(parentId, beforeIndex);
                            }}
                        />
                    )}

                    {isFullWidthRow ? (
                        <div id={rowId} data-testid={rowId} className="tw-flex tw-items-stretch tw-w-full">
                            {canAddSiblings && (
                                <div id={`${rowId}-add-vertical-left-wrapper`} className="tw-px-1">
                                    <AddNestedVertical
                                        id={`${rowId}-add-vertical-left`}
                                        parentId={parentId}
                                        nestedContainers={nestedContainers}
                                        rowContainers={rowContainers}
                                        onAdd={() => handleAddVertical(parentId, rowIndex)}
                                    />
                                </div>
                            )}

                            {renderNestedContainer(rowContainers[0])}

                            {canAddSiblings && (
                                <div id={`${rowId}-add-vertical-right-wrapper`} className="tw-px-1">
                                    <AddNestedVertical
                                        id={`${rowId}-add-vertical-right`}
                                        parentId={parentId}
                                        nestedContainers={nestedContainers}
                                        rowContainers={rowContainers}
                                        onAdd={() => handleAddVertical(parentId, rowIndex)}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div id={rowId} data-testid={rowId} className="tw-flex tw-flex-row tw-items-stretch">
                            {canAddSiblings && (
                                <div id={`${rowId}-add-vertical-start-wrapper`} className="tw-px-1">
                                    <AddNestedVertical
                                        id={`${rowId}-add-vertical-start`}
                                        parentId={parentId}
                                        nestedContainers={nestedContainers}
                                        rowContainers={rowContainers}
                                        onAdd={() => handleAddVertical(parentId, rowIndex)}
                                    />
                                </div>
                            )}

                            {rowContainers.map((nestedContainer: any, colIndex: number) => (
                                <React.Fragment key={nestedContainer.id}>
                                    {renderNestedContainer(nestedContainer)}

                                    {canAddSiblings &&
                                        colIndex < rowContainers.length - 1 && (
                                        <div id={`${rowId}-add-vertical-${colIndex}-wrapper`} className="tw-px-1">
                                            <AddNestedVertical
                                                id={`${rowId}-add-vertical-${colIndex}`}
                                                parentId={parentId}
                                                nestedContainers={nestedContainers}
                                                rowContainers={rowContainers}
                                                onAdd={() => handleAddVertical(parentId, rowIndex)}
                                            />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}

                            {canAddSiblings && (
                                <div id={`${rowId}-add-vertical-end-wrapper`} className="tw-px-1">
                                    <AddNestedVertical
                                        id={`${rowId}-add-vertical-end`}
                                        parentId={parentId}
                                        nestedContainers={nestedContainers}
                                        rowContainers={rowContainers}
                                        onAdd={() => handleAddVertical(parentId, rowIndex)}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {rowIndex < nestedRows.length - 1 && canAddSiblings && (
                        <AddNestedBetweenLine
                            id={`${baseId}-add-between-${rowIndex}`}
                            parentId={parentId}
                            beforeIndex={nestedContainers.findIndex(nc => nc.id === nestedRows[rowIndex + 1][0].id)}
                            onAdd={() => {
                                const firstContainerInNextRow = nestedRows[rowIndex + 1][0];
                                const beforeIndex = nestedContainers.findIndex(nc => nc.id === firstContainerInNextRow.id);
                                console.log(`[AddNestedBetween] Adding between rows at index ${beforeIndex}, parentId: ${parentId}, nestingLevel: ${nestingLevel}`);
                                handleAddBetween(parentId, beforeIndex);
                            }}
                        />
                    )}
                </React.Fragment>
            );
        })}

        {canAddSiblings && (
            <AddNestedHorizontal
                id={`${baseId}-add-horizontal`}
                parentId={parentId}
                onAdd={() => {
                    console.log(`Adding new container to parent ${parentId}`);
                    onAddNestedHorizontal(parentId);
                }}
            />
        )}
    </div>
);   
};